import { useCallback } from 'react';
import { Column } from '@elementary/common-ui/components';
import * as atoms from '@elementary/common-ui/atoms';
import {
  AgentCloudTestConfigChange,
  AgentCloudTestConfigFields,
  AgentTestConfigPlan,
  AgentCloudTestCreateChange,
  AgentTestOperation,
  getAgentTestChangeRowId,
  isAgentCloudTestCreateChange,
} from '@elementary/common-ui/types';
import useTableAssets, {
  TableAssetsData,
} from '@elementary/common-ui/hooks/useTableAssets';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { useRecoilValue } from 'recoil';
import { AgentConfigPreviewShell } from '../agentConfigPreviewShared';
import {
  BREADCRUMB_LABELS,
  COLUMN_SCHEMA,
  STORAGE_KEY,
  TEST_TYPE_FIELDS,
  TEST_TYPE_NAMES,
} from './constants';
import { ConfigFieldKey, ColumnSchema, PreviewRow } from './types';

const buildCreatePreviewRow = (
  change: AgentCloudTestCreateChange,
  asset?: TableAssetsData,
): PreviewRow => ({
  id: getAgentTestChangeRowId(change),
  asset_id: change.assetId,
  schema: asset?.schemaName || '',
  table: asset?.tableName || asset?.name || '',
  package: 'elementary',
  name: TEST_TYPE_NAMES[change.testType],
  type: TEST_TYPE_NAMES[change.testType],
  severity: 'warn',
  owners: asset?.owners || [],
  tags: asset?.tags || [],
  args: {},
  updated_at: '',
  meta: {},
  is_singular: false,
  original_path: '',
  is_cloud: true,
  channels: [],
  model_name: asset?.name || '',
  database_name: asset?.dbName || '',
  materialization: asset?.materialization,
  configChange: change,
  isCreateRow: true,
});

const useCloudCreateRowData = (isCreateOperation: boolean) => {
  const { data: tableAssetsMap, isFetching } = useTableAssets({
    enabled: isCreateOperation,
  });

  const buildCreatePreviewRowForPlan = useCallback(
    (change: AgentCloudTestCreateChange): PreviewRow =>
      buildCreatePreviewRow(change, tableAssetsMap?.get(change.assetId)),
    [tableAssetsMap],
  );

  return {
    buildCreatePreviewRow: buildCreatePreviewRowForPlan,
    isLoading: isFetching,
  };
};

const getPresentFields = (plan: AgentTestConfigPlan): Set<ConfigFieldKey> => {
  const fields = new Set<ConfigFieldKey>();
  if (plan.operation === 'delete') return fields;

  for (const change of plan.changes) {
    const allowedKeys = isAgentCloudTestCreateChange(change)
      ? new Set(TEST_TYPE_FIELDS[change.testType])
      : null;

    for (const { key } of COLUMN_SCHEMA) {
      if (allowedKeys && !allowedKeys.has(key)) continue;
      if (change[key]) {
        fields.add(key);
      }
    }
  }
  return fields;
};

const getSubmitLabel = (
  operation: AgentTestOperation,
  selectedCount: number,
): string => {
  if (operation === 'create') {
    return `Create ${pluralizeString(selectedCount, 'test', 'tests')}`;
  }
  if (operation === 'delete') {
    return `Delete ${pluralizeString(selectedCount, 'test', 'tests')}`;
  }
  return 'Approve';
};

const CloudTestAgentConfigPreview = () => {
  const plan = useRecoilValue(atoms.agentCloudTestConfigPlan);
  const isCreateOperation = plan?.operation === 'create';
  const { buildCreatePreviewRow: buildRow, isLoading: isCreateRowDataLoading } =
    useCloudCreateRowData(isCreateOperation);

  const buildDynamicColumns = useCallback(
    ({
      plan: currentPlan,
      setChange,
      presentFields,
      ownerOptions,
      tagOptions,
      columnBuilders,
    }: Parameters<
      React.ComponentProps<
        typeof AgentConfigPreviewShell<AgentCloudTestConfigChange, PreviewRow>
      >['buildDynamicColumns']
    >[0]) => {
      if (currentPlan.operation === 'delete') return [];

      const getField =
        <K extends ConfigFieldKey>(key: K) =>
        (row: PreviewRow): AgentCloudTestConfigFields[K] | undefined =>
          row.configChange[key];

      const listOptionsMap = { owners: ownerOptions, tags: tagOptions };

      const buildColumnForSchema = (
        schema: ColumnSchema,
      ): Column<PreviewRow> => {
        switch (schema.type) {
          case 'dropdown':
            return columnBuilders.buildDropdownColumn(
              schema.id,
              schema.header,
              schema.options,
              getField(schema.key),
              (row, newValue) =>
                setChange(row.id, (change) => {
                  const field = change[schema.key];
                  if (!field || !('new' in field)) return change;
                  return {
                    ...change,
                    [schema.key]: { ...field, new: newValue },
                  };
                }),
            );
          case 'period':
            return columnBuilders.buildPeriodColumn(
              schema.id,
              schema.header,
              getField(schema.key),
              (row, newValue) =>
                setChange(row.id, (change) => {
                  const field = change[schema.key];
                  if (!field) return change;
                  return {
                    ...change,
                    [schema.key]: { ...field, new: newValue },
                  };
                }),
              schema.nullable,
            );
          case 'boolean':
            return columnBuilders.buildBooleanColumn(
              schema.id,
              schema.header,
              schema.trueLabel,
              schema.falseLabel,
              getField(schema.key),
              (row, newValue) =>
                setChange(row.id, (change) => {
                  const field = change[schema.key];
                  if (!field) return change;
                  return {
                    ...change,
                    [schema.key]: { ...field, new: newValue },
                  };
                }),
            );
          case 'list':
            return columnBuilders.buildListColumn(
              schema.id,
              schema.header,
              schema.itemLabel,
              getField(schema.key),
              (row, newValue) =>
                setChange(row.id, (change) => {
                  const field = change[schema.key];
                  if (!field) return change;
                  return {
                    ...change,
                    [schema.key]: { ...field, new: newValue },
                  };
                }),
              listOptionsMap[schema.optionsKey],
              {
                filterNewToOptions: true,
                isElasticLayout: true,
              },
            );
        }
      };

      return COLUMN_SCHEMA.filter((schema) =>
        presentFields.has(schema.key),
      ).map(buildColumnForSchema);
    },
    [],
  );

  return (
    <AgentConfigPreviewShell
      planAtom={atoms.agentCloudTestConfigPlan}
      isCreateChange={isAgentCloudTestCreateChange}
      buildCreatePreviewRow={(change) => {
        if (!isAgentCloudTestCreateChange(change)) {
          throw new Error('Expected create change');
        }
        return buildRow(change);
      }}
      isCreateRowDataLoading={isCreateRowDataLoading}
      getPresentFields={getPresentFields}
      buildDynamicColumns={buildDynamicColumns}
      getSubmitLabel={getSubmitLabel}
      breadcrumbLabels={BREADCRUMB_LABELS}
      storageKey={STORAGE_KEY}
    />
  );
};

export default CloudTestAgentConfigPreview;
